import { createContainer } from '~/modules/unstated-next-utils/createContainer';
import last from 'lodash/last';
import dayjs from 'dayjs';
import { PROPORTION_COLORS } from '~/modules/tradeSheet/shared/proportionColors';
import { useTimeRangeStore } from '~/modules/tradeSheet/shared/useTimeRangeStore';
import { useWinRate, useReturnRate, useTop3PnL, useFrequency, usePositionDuration, useCommodityProportion, PROPORTION_NAMES, useMaxDrawdown, useProfitFactor, useRiskIndicator, useDailyReturnRate, useProfitFactorPerLot, useFuturesAdviserPerformanceRanking, } from '~/modules/tradeSheet/tradingSummary/useTradingSummaryResource';
import { useTradingSummaryUserAccountState } from '~/modules/tradeSheet/tradingSummary/usetradingSummaryUserAccountState';
/** 實際交易統計數值資料，透過 usetradingSummaryUserAccountState 先取得使用者資訊 */
const useTradingSummary = () => {
    const auth = useTradingSummaryUserAccountState.useContainer();
    const since = useTimeRangeStore(s => s.since);
    const selectedAccount = auth.state.selectedIdAccount;
    const token = auth.state.outsideToken ?? '';
    const options = [
        token,
        auth.state.nationalId ?? '',
        selectedAccount,
        since,
    ];
    const { data: winRate } = useWinRate(...options);
    const { data: profitFactor } = useProfitFactor(...options);
    const { data: profitFactorPerLot } = useProfitFactorPerLot(...options);
    const { data: maxDrawdown } = useMaxDrawdown(...options);
    const { data: riskIndicator } = useRiskIndicator(...options);
    const { data: rawReturnRate } = useReturnRate(...options);
    const { data: rawDailyReturnRate } = useDailyReturnRate(...options);
    const { data: top3PnL } = useTop3PnL(...options);
    const { data: rawFrequency } = useFrequency(...options);
    const { data: rawDuration } = usePositionDuration(...options);
    const { data: rawProportion } = useCommodityProportion(...options);
    const { data: adviserPerformance } = useFuturesAdviserPerformanceRanking(token, since);
    const proportion = {
        home: Object.entries(rawProportion && rawProportion.length >= 1
            ? {
                proportion1: rawProportion[0].proportion1,
                proportion2: rawProportion[0].proportion2,
                proportion3: rawProportion[0].proportion3,
                proportion4: rawProportion[0].proportion4,
                proportion5: rawProportion[0].proportion5,
                proportion6: rawProportion[0].proportion6,
                proportion7: rawProportion[0].proportion7,
                proportion8: rawProportion[0].proportion8,
                proportion9: rawProportion[0].proportion9,
            }
            : {})
            .map(([k, v], i) => ({
            name: typeof PROPORTION_NAMES[i] === 'string' ? PROPORTION_NAMES[i] : PROPORTION_NAMES[i][0],
            value: +v,
            color: PROPORTION_COLORS[i],
        }))
            .filter(p => p.value),
        foreign: Object.entries(rawProportion && rawProportion.length >= 2
            ? {
                proportion1: rawProportion[1].proportion1,
                proportion2: rawProportion[1].proportion2,
                proportion3: rawProportion[1].proportion3,
                proportion4: rawProportion[1].proportion4,
                proportion5: rawProportion[1].proportion5,
                proportion6: rawProportion[1].proportion6,
                proportion7: rawProportion[1].proportion7,
                proportion8: rawProportion[1].proportion8,
                proportion9: rawProportion[1].proportion9,
            }
            : {})
            .map(([k, v], i) => ({
            name: typeof PROPORTION_NAMES[i] === 'string' ? PROPORTION_NAMES[i] : PROPORTION_NAMES[i][1],
            value: +v,
            color: PROPORTION_COLORS[i],
        }))
            .filter(p => p.value),
    };
    const analysisDetails = [
        {
            label: '勝率(%)',
            header: '勝率（獲利交易次數 / 總交易次數）',
            value: winRate && winRate.length >= 1 ? winRate?.[0].winRate.toFixed(1) : 0,
            adjustValue: winRate && winRate.length >= 1 ? winRate?.[0].fubonRanking : 0,
            comparedAdjustValue: adviserPerformance?.winRate ?? 0,
            isPercentage: true,
        },
        {
            label: '每口賺賠比',
            header: '每口賺賠比(平均每口平倉獲利/平均每口平倉虧損)',
            value: profitFactorPerLot && profitFactorPerLot.length >= 1
                ? profitFactorPerLot?.[0].profitFactorPerLot.toFixed(1)
                : 0,
            adjustValue: profitFactorPerLot && profitFactorPerLot.length >= 1
                ? profitFactorPerLot?.[0].fubonRanking
                : 0,
            comparedAdjustValue: adviserPerformance?.profitFactorPerLot ?? 0,
        },
        {
            label: '累計賺賠比',
            header: '累計賺賠比(平倉獲利累計金額/平倉虧損累計金額)',
            value: profitFactor && profitFactor.length >= 1 ? profitFactor?.[0].profitFactor.toFixed(1) : 0,
            adjustValue: profitFactor && profitFactor.length >= 1 ? profitFactor?.[0].fubonRanking : 0,
            comparedAdjustValue: adviserPerformance?.profitFactor ?? 0,
        },
        {
            label: '最大回撤比率(%)',
            header: '最大回撤比率(期間權益淨值損益率拉回之最大比率)',
            value: maxDrawdown && maxDrawdown.length >= 1 ? maxDrawdown?.[0].maxDrawdown.toFixed(1) : 1,
            adjustValue: maxDrawdown && maxDrawdown.length >= 1 ? maxDrawdown?.[0].fubonRanking : 0,
            comparedAdjustValue: adviserPerformance?.maxDrawdown ?? 0,
            isPercentage: true,
        },
        {
            label: '風險指標(%)',
            header: '風險指標(期間平均風險指標)',
            value: riskIndicator && riskIndicator?.length >= 1
                ? riskIndicator?.[0].riskIndicator.toFixed(1)
                : 1,
            adjustValue: riskIndicator && riskIndicator?.length >= 1 ? riskIndicator?.[0].fubonRanking : 100,
            comparedAdjustValue: adviserPerformance?.riskIndicator ?? 100,
            isPercentage: true,
        },
    ];
    const duration = rawDuration ? rawDuration[0] : null;
    const overallRanking = rawReturnRate?.length ? rawReturnRate[0].fubonRanking : 0;
    const lastGroupReturnRate = last(rawDailyReturnRate);
    const groupReturnRates = {
        mine: lastGroupReturnRate?.customerReturnRate,
        txf: lastGroupReturnRate?.txf,
        adviser: lastGroupReturnRate?.futuresAdviser,
    };
    const frequency = rawFrequency ? rawFrequency[0] : null;
    return {
        state: {
            accountNumber: winRate && winRate.length > 0 && winRate[0].accountNo,
            analysisDetails,
            overallRanking,
            groupReturnRates,
            rawDailyReturnRate: rawDailyReturnRate?.sort((a, b) => dayjs(a.date).unix() - dayjs(b.date).unix()),
            top3PnL: top3PnL,
            frequency,
            duration,
            proportion: proportion,
        },
    };
};
export const useTradingSummaryState = createContainer(useTradingSummary);
